import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { fmtDate, type AdminUser } from '../lib/types'
import { StatusBadge } from '../components/StatusBadge'
import { useState } from 'react'

type IdentityDocs = {
  passport_main_url?: string | null
  passport_reg_url?: string | null
  expires_in?: number
}

export function UserDetailPage() {
  const { id } = useParams()
  const qc = useQueryClient()
  const [reason, setReason] = useState('')
  const { data, isLoading, error } = useQuery({
    queryKey: ['user', id],
    queryFn: () => api<AdminUser>(`/admin/users/${id}`),
    enabled: !!id,
  })
  const docs = useQuery({
    queryKey: ['identity-docs', id],
    queryFn: () => api<IdentityDocs>(`/admin/users/${id}/identity/documents`),
    enabled: !!id && !!data && data.identity_status !== 'none',
    staleTime: 60_000,
  })

  const block = useMutation({
    mutationFn: () => api(`/admin/users/${id}/block`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user', id] }),
  })
  const unblock = useMutation({
    mutationFn: () => api(`/admin/users/${id}/unblock`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user', id] }),
  })
  const approve = useMutation({
    mutationFn: () => api(`/admin/users/${id}/identity/approve`, { method: 'POST', body: '{}' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user', id] }),
  })
  const reject = useMutation({
    mutationFn: () => api(`/admin/users/${id}/identity/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
    onSuccess: () => { setReason(''); qc.invalidateQueries({ queryKey: ['user', id] }) },
  })

  if (isLoading) return <p className="muted">Загрузка…</p>
  if (error || !data) return <div className="error">{(error as Error)?.message || 'Не найден'}</div>

  const mainUrl = docs.data?.passport_main_url
  const regUrl = docs.data?.passport_reg_url

  return (
    <div className="stack">
      <div>
        <Link to="/users" className="muted">← Пользователи</Link>
        <h1 className="page-title" style={{ marginTop: 8 }}>{data.name}</h1>
        <p className="page-sub">{data.email} · {data.account_type === 'customer' ? 'Заказчик' : 'Исполнитель'}</p>
      </div>

      <div className="two-col">
        <div className="card stack">
          <div className="row">
            <StatusBadge value={data.identity_status} />
            {data.is_blocked
              ? <StatusBadge value="blocked" label="Заблокирован" />
              : <StatusBadge value="approved" label="Активен" />}
          </div>
          <div><span className="muted">Город:</span> {[data.city, data.country].filter(Boolean).join(', ') || '—'}</div>
          <div><span className="muted">Регистрация:</span> {fmtDate(data.created_at)}</div>
          <div><span className="muted">Последняя активность:</span> {fmtDate(data.last_active_at)}</div>
          <div><span className="muted">Рейтинг:</span> {data.rating_avg} ({data.rating_count})</div>
          <div><span className="muted">Сдал / принял:</span> {data.completed_orders_count} / {data.accepted_orders_count}</div>
          {data.headline && <div><span className="muted">О себе:</span> {data.headline}</div>}
          {data.identity_reject_reason && (
            <div className="error">Причина отказа: {data.identity_reject_reason}</div>
          )}
          <div className="row">
            {data.is_blocked
              ? <button className="btn btn-success" onClick={() => unblock.mutate()}>Разблокировать</button>
              : <button className="btn btn-danger" onClick={() => block.mutate()}>Заблокировать</button>}
          </div>
        </div>

        <div className="card stack">
          <h3 style={{ margin: 0 }}>Верификация</h3>
          {docs.isLoading && <div className="muted">Загрузка документов…</div>}
          {docs.isError && <div className="error">Не удалось получить подписанные ссылки</div>}
          <div className="doc-grid">
            <div>
              <div className="muted" style={{ marginBottom: 6 }}>Первая страница</div>
              {mainUrl
                ? <img src={mainUrl} alt="passport main" />
                : <div className="muted">Нет файла</div>}
            </div>
            <div>
              <div className="muted" style={{ marginBottom: 6 }}>Регистрация</div>
              {regUrl
                ? <img src={regUrl} alt="passport reg" />
                : <div className="muted">Нет файла</div>}
            </div>
          </div>
          {(data.identity_status === 'pending' || data.identity_status === 'rejected') && (
            <div className="stack">
              <div className="row">
                <button className="btn btn-success" onClick={() => approve.mutate()}>Одобрить</button>
              </div>
              <textarea
                className="input"
                style={{ minHeight: 80, width: '100%' }}
                placeholder="Причина отклонения"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <button className="btn btn-danger" disabled={!reason.trim()} onClick={() => reject.mutate()}>
                Отклонить
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
